import { useState, type ReactNode } from 'react'
import { RightControllerIllustration, SENSORES_STORE_URL } from '@versports/ui'
import { PLAYER_SETUP_STEPS } from '../setup/playerSetupSteps'

/**
 * Setup sin kit (desde 2026-09-28 el único): el Polar H10 se conecta directo
 * al visor por la app VerSports Sensores (repo vrs-quest-sensores, vrs-docs
 * 58). No hay base RPi, Wi-Fi del kit ni GSR.
 */
type StepId = 'polar' | 'quest-app' | 'control' | 'visor'

const STEPS: readonly StepId[] = ['polar', 'quest-app', 'control', 'visor']
const playerStepById = { polar: 0, control: 1, visor: 2 } as const
const stepLabels: Record<StepId, string> = {
  polar: 'Colocarse el Polar',
  'quest-app': 'Abrir la app del visor',
  control: 'Agarrar el control',
  visor: 'Ponerse el visor',
}

/**
 * Abre la app del visor desde el Quest Browser (intent de Android con el
 * esquema `versports-sensores` que declara la app, repo vrs-quest-sensores). Si la app no
 * está instalada, el navegador vuelve a esta guía.
 */
export const OPEN_COMPANION_URL =
  'intent://abrir#Intent;scheme=versports-sensores;package=com.versports.questcompanion;' +
  `S.browser_fallback_url=${encodeURIComponent('https://ver-sports.com/guia')};end`

const title: Record<StepId, string> = {
  polar: PLAYER_SETUP_STEPS[0]!.title,
  'quest-app': 'Abrí VerSports Sensores en el visor',
  control: PLAYER_SETUP_STEPS[1]!.title,
  visor: PLAYER_SETUP_STEPS[2]!.title,
}
const illustrations: Record<StepId, { image?: string; alt: string }> = {
  polar: { image: PLAYER_SETUP_STEPS[0]!.image, alt: PLAYER_SETUP_STEPS[0]!.alt },
  'quest-app': { image: PLAYER_SETUP_STEPS[2]!.image, alt: 'Visor Meta Quest con la app VerSports Sensores' },
  control: { alt: PLAYER_SETUP_STEPS[1]!.alt },
  visor: { image: PLAYER_SETUP_STEPS[2]!.image, alt: PLAYER_SETUP_STEPS[2]!.alt },
}
const expected: Partial<Record<StepId, string>> = {
  polar: 'Todavía no se ve nada: el visor busca el Polar recién cuando VerSports está abierto, en el paso siguiente.',
  'quest-app': 'La app muestra “Servicio: activo” y abre VerSports en el navegador del visor. En la pantalla previa al entrenamiento, el indicador Polar pasa a “Conectado” solo.',
  visor: 'El visor entra con el PIN del jugador. El biofeedback usa el pulso del Polar.',
}
const help: Partial<Record<StepId, ReactNode>> = {
  polar: <>
    <ol className="list-decimal space-y-sm pl-lg">
      <li>Revisá los dos broches, los electrodos húmedos y el contacto firme con la piel: sin contacto el Polar no transmite.</li>
      <li>Si el Polar está conectado a un celular o reloj (por ejemplo con Polar Flow), desconectalo: puede tenerlo tomado.</li>
      <li>La app VerSports Sensores muestra los equipos que ve el visor. Si el Polar no figura, el problema está en la banda, no en el visor.</li>
    </ol>
    <a className="mt-md inline-block text-sm underline" href="https://support.polar.com/en/troubleshooting-polar-h9-h10-heart-rate-sensor" target="_blank" rel="noopener noreferrer">Ayuda oficial de Polar H10</a>
  </>,
  'quest-app': <p>Si VerSports Sensores no aparece en la Biblioteca, este visor todavía no tiene la app: instalala desde la <a className="underline" href={SENSORES_STORE_URL} target="_blank" rel="noopener noreferrer">Meta Horizon Store</a> (se hace una vez por visor). Si la ficha no abre, la cuenta Meta del visor no está invitada: pedile la invitación al responsable. Si el navegador bloqueó el acceso a la red local, habilitalo en la configuración del sitio ver-sports.com y volvé a tocar Abrir VerSports.</p>,
  control: <p>Si no aparece el puntero, revisá que el control tenga pila y esté conectado al visor. Pedile ayuda al staff para configurarlo.</p>,
  visor: <p>Si el indicador Polar no pasa a “Conectado”, volvé a abrir la app VerSports Sensores: su pantalla dice qué está pasando con el Polar.</p>,
}

/** Standalone, linear instructions. Each screen has one action and its own illustration. */
export function SetupGuide() {
  const [step, setStep] = useState<StepId>(STEPS[0]!)
  const index = STEPS.indexOf(step)
  const playerStep = step in playerStepById ? PLAYER_SETUP_STEPS[playerStepById[step as keyof typeof playerStepById]] : undefined
  const illustration = illustrations[step]
  const nextStep = () => setStep(STEPS[Math.min(index + 1, STEPS.length - 1)]!)
  const previousStep = () => setStep(STEPS[Math.max(index - 1, 0)]!)

  return (
    <div className="mx-auto max-w-3xl px-md py-xl sm:px-xl">
      <header className="mb-xl">
        <p className="text-sm font-semibold uppercase tracking-widest text-text-muted">Antes de entrenar</p>
        <h1 className="mt-sm text-3xl font-bold">Guía de setup</h1>
      </header>

      <article className="card p-lg sm:p-xl" aria-label={`Paso ${index + 1}: ${stepLabels[step]}`}>
        <div aria-live="polite" aria-atomic="true">
          <p className="text-sm font-semibold text-text-muted">Paso {index + 1} de {STEPS.length}</p>
          <h2 className="mt-sm font-display text-xl font-bold normal-case tracking-tight text-text-primary">{title[step]}</h2>
        </div>

        <figure className="my-lg flex h-48 items-center justify-center sm:h-56" aria-label={`Ilustración: ${stepLabels[step]}`}>
          {illustration.image
            ? <img src={illustration.image} alt={illustration.alt} className="h-full w-full max-w-sm object-contain" />
            : <div className="w-full max-w-[18rem] sm:max-w-[20rem]"><RightControllerIllustration /></div>}
        </figure>

        <div className="space-y-md text-lg text-text-secondary">
          {step === 'quest-app' && <>
            <p>Desde el navegador del visor tocá <strong>Abrir VerSports Sensores</strong>, o buscala en la <strong>Biblioteca</strong> del visor (si se instaló por USB, en <strong>Orígenes desconocidos</strong>). En la app, tocá <strong>Abrir VerSports</strong>.</p>
            <a className="btn inline-block" href={OPEN_COMPANION_URL}>Abrir VerSports Sensores</a>
            <p className="text-base">¿El visor no tiene la app? <a className="underline" href={SENSORES_STORE_URL} target="_blank" rel="noopener noreferrer">Instalala desde la Meta Horizon Store</a>. La cuenta Meta del visor tiene que estar invitada por VerSports.</p>
            <p>La primera vez, dá el permiso de Bluetooth y, cuando el navegador lo pida, permití el acceso a la red local.</p>
          </>}
          {playerStep && <p>{playerStep.detail}</p>}
        </div>

        {expected[step] && <section className="mt-xl rounded-lg border border-primary/30 bg-primary/10 p-lg" aria-label="Resultado esperado">
          <h3 className="font-semibold">Qué debería pasar</h3>
          <p className="mt-sm text-text-secondary">{expected[step]}</p>
          {(step === 'quest-app' || step === 'visor') && <p className="mt-sm text-sm text-text-secondary">La guía no mide la conexión. Revisá el estado real en la pantalla previa a “Comenzar”.</p>}
        </section>}

        {help[step] && <details key={step} className="mt-lg rounded-lg border border-outline p-lg">
          <summary className="cursor-pointer font-semibold">Si no pasa lo esperado</summary>
          <div className="mt-md text-text-secondary">{help[step]}</div>
        </details>}

        <div className="mt-xl flex gap-md">
          <button type="button" className="btn flex-1" disabled={index === 0} onClick={previousStep}>Atrás</button>
          {step !== 'visor'
            ? <button type="button" className="btn-primary flex-1" onClick={nextStep}>Siguiente</button>
            : <a className="btn-primary flex-1 text-center" href="/vr/?companion=1">Ir a VR</a>}
        </div>
      </article>
      <p className="mt-lg text-center text-sm text-text-muted">Podés volver a esta guía desde la pestaña Guía. No hace falta iniciar sesión.</p>
    </div>
  )
}
